import axios from "axios";
import { APIS_URL } from "./Config";

class HttpService {
    apiBaseURL = APIS_URL;

    defaultHeaders = {
        "Content-Type": "application/json",
    };

    fileHeaders = {
        "Content-Type": "multipart/form-data",
    };

    constructor() {
        this.api = axios.create({
            baseURL: this.apiBaseURL,
            headers: this.defaultHeaders,
            timeout: 180000,
        });

        this.setupInterceptors();
    }

    setupInterceptors() {
        this.api.interceptors.request.use(
            (config) => {
                let authToken = localStorage.getItem("authToken");
                if (!authToken) {
                    authToken = sessionStorage.getItem("authToken");
                }
                if (authToken) {
                    config.headers.Authorization = `Bearer ${authToken}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        this.api.interceptors.response.use(
            (response) => response,
            (error) => this.handleAxiosError(error)
        );
    }

    async get(url, queryParams = {}, customHeaders = {}) {
        const headers = { ...this.defaultHeaders, ...customHeaders };
        try {
            const response = await this.api.get(url, {
                headers,
                params: queryParams,
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async post(url, data = {}, queryParams = {}, customHeaders = {}, mediaType = null) {
        const headers = { ...this.defaultHeaders, ...customHeaders };
        const config = {
            headers,
            params: queryParams,
        };

        if (mediaType) {
            config.responseType = "blob";
        }

        try {
            const response = await this.api.post(url, data, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async postFile(url, formData, queryParams = {}, customHeaders = {}) {
        const headers = { ...this.fileHeaders, ...customHeaders };
        try {
            const response = await this.api.post(url, formData, {
                headers,
                params: queryParams,
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async delete(url, queryParams = {}, customHeaders = {}) {
        const headers = { ...this.defaultHeaders, ...customHeaders };
        try {
            const response = await this.api.delete(url, {
                headers,
                params: queryParams,
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    handleAxiosError(error) {
        if (error.response) {
            const status = error.response.status;

            if (status === 401) {
                try {
                    localStorage.clear();
                    sessionStorage.clear();
                } catch (e) {
                }
                if (typeof window !== 'undefined') {
                    window.location.href = '/';
                }
                return Promise.reject({ message: 'Your session has expired. Please log in again.' });
            }

            // Maintenance page can be handled by frontend separately
            if (status === 502) {
                return Promise.reject({ maintenanceMode: true });
            }

            // Payment blocked (booking already rejected/cancelled) — resolve so the caller
            // can inspect the response and show a meaningful message rather than a generic error.
            if (status === 409) {
                return Promise.resolve({ data: { ...error.response.data, paymentBlocked: true } });
            }

            // Provide fallback server error
            const message = error.response.data?.msg || "Server Error. Please try again later.";
            return Promise.reject({ message });
        }

        if (error.request) {
            return Promise.reject({ message: "No response from server." });
        }

        return Promise.reject({ message: error.message || "Unexpected error occurred." });
    }
}

export const httpService = new HttpService();
